import React, { useState } from 'react';
import { Calendar, MapPin, Clock, Phone, CheckCircle, XCircle, AlertCircle, Home as HomeIcon } from 'lucide-react';

const HomeVisits = () => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '',
    visitDate: '',
    visitTime: '',
    purpose: '',
    notes: '',
  });

  const mockPatients = [
    { id: '1', name: 'Mrs. Ngozi Okeke', phone: '+234 803 456 7890', condition: 'Prenatal Care' },
    { id: '2', name: 'Mrs. Fatima Bello', phone: '+234 805 234 5678', condition: 'Postnatal Care' },
    { id: '3', name: 'Mrs. Adaobi Nwankwo', phone: '+234 806 789 0123', condition: 'Pregnant - 12 weeks' },
    { id: '4', name: 'Mrs. Aisha Mohammed', phone: '+234 807 890 1234', condition: 'Child Health' },
  ];

  const mockHomeVisits = [
    {
      id: '1',
      patient: 'Mrs. Ngozi Okeke',
      patientId: 'patient1',
      phone: '+234 803 456 7890',
      address: '15 Ademola Street, Ikeja, Lagos',
      date: '2026-01-31',
      time: '10:00 AM',
      purpose: 'Antenatal Home Check',
      status: 'pending',
      notes: 'Patient requested home visit due to mobility issues',
    },
    {
      id: '2',
      patient: 'Mrs. Fatima Bello',
      patientId: 'patient2',
      phone: '+234 805 234 5678',
      address: '42 Ogunlana Drive, Surulere, Lagos',
      date: '2026-01-30',
      time: '02:00 PM',
      purpose: 'Postnatal Follow-up',
      status: 'confirmed',
      notes: 'Post-delivery check-up',
    },
    {
      id: '3',
      patient: 'Mrs. Adaobi Nwankwo',
      patientId: 'patient3',
      phone: '+234 806 789 0123',
      address: '8 Allen Avenue, Ikeja, Lagos',
      date: '2026-01-29',
      time: '09:00 AM',
      purpose: 'Vitals Check & Blood Pressure Monitoring',
      status: 'completed',
      notes: 'Regular monitoring required',
    },
    {
      id: '4',
      patient: 'Mrs. Aisha Mohammed',
      patientId: 'patient4',
      phone: '+234 807 890 1234',
      address: '23 Herbert Macaulay Way, Yaba, Lagos',
      date: '2026-01-28',
      time: '11:00 AM',
      purpose: 'Child Health Assessment',
      status: 'cancelled',
      notes: 'Patient cancelled - rescheduling needed',
    },
  ];

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: <AlertCircle className="w-4 h-4" /> },
      confirmed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <CheckCircle className="w-4 h-4" /> },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircle className="w-4 h-4" /> },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle className="w-4 h-4" /> },
    };
    return badges[status] || badges.pending;
  };

  const handleScheduleVisit = (e) => {
    e.preventDefault();
    console.log('Scheduling home visit:', formData);
    // In production, this would send notification to patient for approval
    alert('Home visit scheduled! Patient will be notified for approval.');
    setShowScheduleModal(false);
    setFormData({
      patientId: '',
      visitDate: '',
      visitTime: '',
      purpose: '',
      notes: '',
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="p-6 md:p-8 min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl shadow-lg shadow-emerald-500/30">
                <HomeIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  Home Visits
                </h1>
                <p className="text-gray-600 mt-1">Schedule and manage home visits for patients</p>
              </div>
            </div>
            <button
              onClick={() => setShowScheduleModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/30"
            >
              Schedule Visit
            </button>
          </div>
        </div>

        {/* Visits List */}
        <div className="space-y-4">
          {mockHomeVisits.map(visit => {
            const statusBadge = getStatusBadge(visit.status);
            return (
              <div
                key={visit.id}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-emerald-50 rounded-xl">
                        <MapPin className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{visit.patient}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusBadge.bg} ${statusBadge.text} flex items-center gap-1`}>
                            {statusBadge.icon}
                            {visit.status}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-3">{visit.purpose}</p>
                        
                        <div className="grid md:grid-cols-2 gap-3">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4 text-emerald-600" />
                            <span>{new Date(visit.date).toLocaleDateString('en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric'
                            })}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="w-4 h-4 text-emerald-600" />
                            <span>{visit.time}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <MapPin className="w-4 h-4 text-emerald-600" />
                            <span className="truncate">{visit.address}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4 text-emerald-600" />
                            <span>{visit.phone}</span>
                          </div>
                        </div>
                        
                        {visit.notes && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-700"><strong>Notes:</strong> {visit.notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {visit.status === 'pending' && (
                      <div className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg font-medium">
                        Awaiting Patient Approval
                      </div>
                    )}
                    {visit.status === 'confirmed' && (
                      <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-all">
                        Mark Complete
                      </button>
                    )}
                    {visit.status === 'completed' && (
                      <button className="px-4 py-2 border-2 border-emerald-600 text-emerald-600 rounded-lg font-medium hover:bg-emerald-50 transition-all">
                        View Report
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Schedule Visit Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Schedule Home Visit</h2>
                <p className="text-gray-600 mt-1">Request a home visit for a patient</p>
              </div>

              <form onSubmit={handleScheduleVisit} className="p-6 space-y-6">
                {/* Patient Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select Patient *
                  </label>
                  <select
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="">Choose a patient...</option>
                    {mockPatients.map(patient => (
                      <option key={patient.id} value={patient.id}>
                        {patient.name} - {patient.condition}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Visit Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Visit Date *
                    </label>
                    <input
                      type="date"
                      name="visitDate"
                      value={formData.visitDate}
                      onChange={handleChange}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>

                  {/* Visit Time */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Visit Time *
                    </label>
                    <input
                      type="time"
                      name="visitTime"
                      value={formData.visitTime}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Purpose */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Purpose of Visit *
                  </label>
                  <input
                    type="text"
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Antenatal check-up, Postnatal care, etc."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Any special instructions or requirements..."
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> The patient will receive a notification and must approve the home visit request by providing their address.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/30"
                  >
                    Schedule Visit
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowScheduleModal(false)}
                    className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeVisits;
